import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Project from '../../common/Utils/project';
import { useAuth } from 'oidc-react';
import Pagination from '../../components/Pagination/Pagination';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import api from '../../components/services/apiService';
import { DataTable, ColumnConfig } from '../../components/Table';
import { toBase64 } from '../../common/Utils/base64';
import { downloadCSV as downloadCSVFile } from '../../common/Utils/downloadHelpers';
import SoftwarePolicyModal from '../../components/Modal/SoftwarePolicyModal';
import DynamicDataTooltip from '../../components/DynamicDataTooltip/DynamicDataTooltip';
import { useToast } from '../../providers/ToastProvider/ToastContext';
import {
  checkSoftwarePolicies,
  submitSoftwarePolicies,
  SoftwarePolicy,
  PolicyMode,
} from '../../components/services/softwareComplianceService';

const itemsPerPage = 20;
// Matches the AssetManagementAPI's PerformanceSettings:MaxPageSize.
const CSV_PAGE_SIZE = 100;
interface PaginationInfo {
  pageNumber: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}
type Asset = {
  softwareAssetInventoryId: string;
  name: string;
  packageIdentifier?: string;
  version: string;
  agents: any[];
  insatlledServers: number;
  isSupported: boolean;
  insatlledEndpoints: number;
  isManaged: boolean;
};

type TenantGroup = {
  tenantGroupId: string;
  groupName: string;
  isActive: boolean;
  isDefault: boolean;
};

const AssetsTable: React.FC = () => {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [searchParams, setSearchParams] = useSearchParams();
  const supportedParam = searchParams.get('supported');
  const [isSupported, setIsSupported] = useState<boolean | null>(
    supportedParam === 'true' ? true : supportedParam === 'false' ? false : true, // default to true if not set
  );
  const [showDropdown, setShowDropdown] = useState(isSupported ?? false);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationInfo, setPaginationInfo] = useState<PaginationInfo | null>(null);
  const [assetsList, setAssetsList] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  // Separate from `loading` so exporting doesn't swap the table for its loading state.
  const [exporting, setExporting] = useState(false);
  const { selectedTenantId } = useTenantSelection();
  const [toggleLoading, setToggleLoading] = useState(false);
  const [policyData, setPolicyData] = useState<Record<string, SoftwarePolicy>>({});
  const [policyLoading, setPolicyLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [policyMode, setPolicyMode] = useState<PolicyMode>(PolicyMode.Blacklist);
  const [selectAllGroups, setSelectAllGroups] = useState(false);
  const [tenantGroups, setTenantGroups] = useState<TenantGroup[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set());
  const [submittingPolicy, setSubmittingPolicy] = useState(false);
  const [selectedPolicyAction, setSelectedPolicyAction] = useState<PolicyMode | ''>('');
  const { addToast } = useToast();

  const navigate = useNavigate();
  const auth = useAuth();

  const getSoftwareAssetsList = async (page = 1) => {
    try {
      if (auth && auth.userData) {
        setLoading(true);
        setToggleLoading(true);

        const queryParams = new URLSearchParams({
          pageNumber: page.toString(),
          pageSize: itemsPerPage.toString(),
          groupByPackage: 'true',
        });

        if (search.trim()) {
          queryParams.append('search', search.trim());
        }

        if (isSupported !== null) {
          queryParams.append('isSupported', String(isSupported));
        }

        const response = await api.get(
          `${Project.api}assetmgtservice/softwareassetinventory/${selectedTenantId}/software-assets?${queryParams}`,
        );

        const { data } = response;

        setAssetsList(Array.isArray(data?.data) ? data.data : []);
        setPaginationInfo(data?.pagination ?? null);

        // Fetch policy data for the current page
        if (Array.isArray(data?.data) && data.data.length > 0) {
          await fetchAndSetPolicyData(data.data);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
      setToggleLoading(false);
    }
  };

  const fetchAndSetPolicyData = async (assets: Asset[]) => {
    try {
      setPolicyLoading(true);
      const softwareNames = assets.map((asset) => asset.name);
      const policies = await checkSoftwarePolicies(selectedTenantId!, softwareNames);
      const policyMap: Record<string, SoftwarePolicy> = {};
      policies.forEach((policy) => {
        policyMap[policy.softwareName] = policy;
      });
      setPolicyData(policyMap);
    } catch (error) {
      console.error('Error fetching policy data:', error);
    } finally {
      setPolicyLoading(false);
    }
  };

  // Exports the tenant's entire inventory (managed and unmanaged), not just the current page:
  // the API clamps pageSize to PerformanceSettings:MaxPageSize (100), so walk every page.
  const downloadCSV = async () => {
    if (!selectedTenantId || exporting) return;
    try {
      setExporting(true);

      const allAssets: Asset[] = [];
      let page = 1;
      let totalPages = 1;
      do {
        const response = await api.get(`${Project.api}assetmgtservice/softwareassetinventory/${selectedTenantId}/software-assets`, {
          params: { pageNumber: page, pageSize: CSV_PAGE_SIZE },
        });
        const pageData: Asset[] = Array.isArray(response.data?.data) ? response.data.data : [];
        allAssets.push(...pageData);
        totalPages = response.data?.pagination?.totalPages ?? 1;
        if (pageData.length === 0) break;
        page++;
      } while (page <= totalPages);

      if (allAssets.length === 0) {
        addToast('No software found to export.', 'info', 4000);
        return;
      }

      const rows = allAssets.map((item) => ({
        name: item.name,
        managed: item.isSupported ? 'Managed' : 'Unmanaged',
        installedEndpoints: item.insatlledEndpoints,
      }));

      downloadCSVFile(rows, `software_assets_${new Date().toISOString().slice(0, 10)}`, {
        name: 'Software',
        managed: 'Managed / Unmanaged',
        installedEndpoints: 'Installed Endpoints',
      });
    } catch (error) {
      console.error('Failed to download CSV', error);
      addToast('Failed to download CSV.', 'error', 4000);
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    getSoftwareAssetsList(currentPage);
  }, [currentPage, search, isSupported]);

  useEffect(() => {
    fetchTenantGroups();
  }, [selectedTenantId]);

  const fetchTenantGroups = async () => {
    try {
      if (auth && auth.userData && selectedTenantId) {
        const response = await api.get(`${Project.api}tenantservice/TenantGroup/${selectedTenantId}/groups?includeInactive=true`);
        const { data } = response;
        if (data?.isSuccess && Array.isArray(data?.data)) {
          const groups = data.data[0]?.tenantGroupList || [];
          setTenantGroups(groups.filter((g: TenantGroup) => g.isActive));
        }
      }
    } catch (error) {
      console.error('Error fetching tenant groups:', error);
    }
  };

  const handleSelectAll = () => {
    const selectableAssets = assetsList.filter((asset) => asset.packageIdentifier !== 'Defatom.Agent');

    if (selectedItems.size === selectableAssets.length) {
      setSelectedItems(new Set());
    } else {
      const allNames = new Set(selectableAssets.map((asset) => asset.name));
      setSelectedItems(allNames);
    }
  };

  const handleSelectItem = (name: string) => {
    const asset = assetsList.find((a) => a.name === name);

    if (asset?.packageIdentifier === 'Defatom.Agent') {
      return;
    }

    const newSelected = new Set(selectedItems);

    if (newSelected.has(name)) {
      newSelected.delete(name);
    } else {
      newSelected.add(name);
    }

    setSelectedItems(newSelected);
  };

  const getSquareStyle = (status: boolean) => {
    let backgroundColor = 'transparent';

    if (status === true) {
      backgroundColor = '#00FF00';
    }

    return {
      width: '20px',
      height: '20px',
      borderRadius: '4px',
      border: status ? '#00FF00' : '5px solid #444',
      backgroundColor,
      cursor: 'pointer',
      display: 'inline-block',
      marginLeft: '35px',
    };
  };

  const openPolicyModal = (mode: PolicyMode) => {
    if (selectedItems.size === 0) {
      alert('Please select at least one software');
      return;
    }
    setPolicyMode(mode);
    setShowPolicyModal(true);
    setSelectAllGroups(false);
    setSelectedGroups(new Set());
  };

  const handleSubmitPolicy = async (modeOrEvent?: PolicyMode | React.SyntheticEvent, groups: string[] = Array.from(selectedGroups)) => {
    let mode: PolicyMode = policyMode;
    // If first argument is a string, it's the mode; if it's an event, ignore
    if (typeof modeOrEvent === 'string') {
      mode = modeOrEvent;
    } else if (modeOrEvent && typeof (modeOrEvent as any).preventDefault === 'function') {
      // It's an event, ignore
      mode = policyMode;
    }
    try {
      setSubmittingPolicy(true);

      const selectedAssets = assetsList.filter((asset) => selectedItems.has(asset.name));
      const updatedByClaim = auth?.userData?.profile?.name;
      const updatedBy = typeof updatedByClaim === 'string' ? updatedByClaim : '';

      const payload = selectedAssets.map((asset) => ({
        packageIdentifier: asset.packageIdentifier || policyData[asset.name]?.packageIdentifier || '',
        softwareName: asset.name,
        policyMode: mode,
        groupIdList: groups,
        updatedBy,
      }));

      const result = await submitSoftwarePolicies(selectedTenantId!, payload);

      if (result?.isSuccess) {
        addToast(result.message || 'Policy updated successfully', 'success');
        setShowPolicyModal(false);
        setSelectedItems(new Set());
        // Refresh policy data
        await getSoftwareAssetsList(currentPage);
      }
    } catch (error) {
      console.error('Error submitting policy:', error);
      addToast('Failed to update policy. Please try again.', 'error');
    } finally {
      setSubmittingPolicy(false);
    }
  };

  const totalPages = paginationInfo?.totalPages ?? 1;

  const handlePageChange = (page: number) => {
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  };

  const routeInfo = (item: Asset) => {
    const base65Id = toBase64(item.name);
    const mode = policyData[item.name]?.policyMode;
    const isBlackListed = mode === PolicyMode.Blacklist;
    navigate(`/SoftwareAssets/software-info/${base65Id}`, { state: { isBlackListed } });
  };

  const allSelected = assetsList.length > 0 && selectedItems.size === assetsList.length;

  // DataTable columns
  const columns: ColumnConfig[] = [
    {
      key: 'checkbox',
      header: <div onClick={handleSelectAll} style={getSquareStyle(allSelected)} />,
      renderHeader: () => <div onClick={handleSelectAll} style={getSquareStyle(allSelected)} />,
      width: '6%',
      align: 'center',
      render: (value, item: Asset) => {
        const isDisabled = item.packageIdentifier === 'Defatom.Agent';

        return (
          <div
            onClick={() => {
              if (!isDisabled) {
                handleSelectItem(item.name);
              }
            }}
            style={{
              ...getSquareStyle(selectedItems.has(item.name)),
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              opacity: isDisabled ? 0.4 : 1,
            }}
          />
        );
      },
    },
    {
      key: 'name',
      header: 'Software',
      renderHeader: () => <span style={{ display: 'block', paddingLeft: 12, width: '100%', textAlign: 'left' }}>Software</span>,
      width: '28%',
      render: (value: any, item: Asset) => (
        <span
          style={{
            cursor: 'pointer',
            paddingLeft: 12,
            display: 'block',
            lineHeight: 1.35,
            overflowWrap: 'anywhere',
          }}
          onClick={() => routeInfo(item)}
        >
          {item.name}
        </span>
      ),
    },
    // {
    //   key: 'version',
    //   header: 'Version',
    //   renderHeader: () => <span style={{ display: 'block', width: '100%', textAlign: 'center' }}>Version</span>,
    //   width: '14%',
    //   align: 'center',
    //   render: (value) => value,
    // },
    {
      key: 'insatlledEndpoints',
      header: 'Installed Endpoints',
      renderHeader: () => <span style={{ display: 'block', width: '100%', textAlign: 'center' }}>Installed Endpoints</span>,
      width: '24%',
      align: 'center',
      render: (value) => value,
    },
    // {
    //   key: 'insatlledServers',
    //   header: 'Installed Servers',
    //   renderHeader: () => <span style={{ display: 'block', width: '100%', textAlign: 'center' }}>Installed Servers</span>,
    //   width: '21.5%',
    //   align: 'center',
    //   render: (value) => value,
    // },
    {
      key: 'isSupported',
      header: 'Supported',
      renderHeader: () => <span style={{ display: 'block', width: '100%', textAlign: 'center' }}>Supported</span>,
      width: '16%',
      align: 'center',
      render: (value, item: Asset) => (
        <div className="flex justify-center items-center">
          <div className={`w-3 h-3 rounded-full ${!item.isSupported ? 'bg-red-500' : 'bg-green-500'}`}></div>
        </div>
      ),
    },
    {
      key: 'policyMode',
      header: 'App Lock',
      renderHeader: () => <span style={{ display: 'block', paddingRight: 12, width: '100%', textAlign: 'center' }}>APP LOCK</span>,
      width: '16%',
      align: 'center',
      render: (value, item: Asset) => {
        const policy = policyData[item.name];

        if (policyLoading) {
          return (
            <div className="flex justify-center items-center">
              <div className="animate-pulse bg-[#1F1F1F] border border-[#333333] h-9 w-32 rounded-full shadow-[0_0_8px_rgba(255,255,255,0.04)]"></div>
            </div>
          );
        }

        if (!policy) {
          return <span className="text-gray-400">-</span>;
        }

        const isAllowlist = policy.policyMode === PolicyMode.Allowlist;
        const isPartial = policy.policyMode === PolicyMode.Partial;
        const blockedVersionsCount = (policy.blockedVersions || []).filter((v) => v && v !== '-').length;
        const blockedGroupsCount = (policy.groupIdList || policy.groups || []).length;
        const policyLabel = isAllowlist ? 'ALLOWED' : isPartial ? 'PARTIAL' : 'BLOCKED';

        const badge = <span>{policyLabel}</span>;

        return (
          <div className="flex justify-center items-center">
            <div
              className={`inline-flex min-w-[140px] items-center justify-center rounded-md text-[11px] font-semibold uppercase tracking-[0.12em] ${
                isAllowlist ? 'text-[#52FFA8]' : isPartial ? 'text-[#FFD66B]' : 'text-[#FF9B9B]'
              }`}
            >
              <div className="flex items-center gap-2">
                {isAllowlist ? (
                  badge
                ) : (
                  <DynamicDataTooltip
                    data={{
                      blockedVersionsCount,
                      blockedGroupsCount,
                    }}
                    fieldLabels={{
                      blockedVersionsCount: 'Blocked Versions Count',
                      blockedGroupsCount: 'Blocked Groups Count',
                    }}
                  >
                    {badge}
                  </DynamicDataTooltip>
                )}
              </div>
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div className="min-h-screen text-white p-6 bg-[#000]">
      <div className="p-4 rounded-lg shadow-lg">
        {/* Search + Buttons */}
        <div className="flex flex-col md:flex-row justify-between mb-4 gap-2">
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Search"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value); // only update local input
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setSearch(searchInput); // apply search only on Enter
                  setCurrentPage(1); // reset to page 1
                }
              }}
              className="w-full md:w-[380px] px-4 py-2 rounded bg-[#1C1C1D] text-white placeholder-white border border-gray-600"
            />
          </div>
          <div className="flex gap-4 justify-center items-center">
            <span className={`ml-3 text-base font-medium ${!showDropdown ? 'text-white' : 'text-gray-400'}`}> Not Supported</span>
            <label className="relative flex items-center mb-0 cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={showDropdown}
                disabled={toggleLoading}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setShowDropdown(checked);
                  setIsSupported(checked);

                  searchParams.set('supported', checked ? 'true' : 'false');
                  setSearchParams(searchParams);
                  setCurrentPage(1);
                }}
              />
              <div className="w-14 h-7 bg-red-500 peer-focus:outline-none rounded-full peer transition-all ease-in-out duration-300 peer-checked:after:translate-x-[28px] after:content-[''] after:absolute after:top-[3px] after:left-[3px] after:bg-white after:rounded-full after:h-[22px] after:w-[22px] after:transition-all after:shadow-md peer-checked:bg-red-500"></div>
              <span className={`ml-3 text-base font-medium ${showDropdown ? 'text-white' : 'text-gray-400'}`}>Supported</span>
            </label>
            <button
              type="button"
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded disabled:opacity-60 disabled:cursor-not-allowed"
              onClick={downloadCSV}
              disabled={exporting}
            >
              {exporting ? 'Downloading...' : 'Download CSV'}
            </button>
            <div className="flex items-center gap-3">
              {/* Select Box */}
              <div className="relative">
                <select
                  disabled={selectedItems.size === 0}
                  value={selectedPolicyAction}
                  onChange={(e) => setSelectedPolicyAction(e.target.value as PolicyMode | '')}
                  className="appearance-none min-w-[180px] px-4 py-2 pr-10 rounded-lg bg-[#1A1A1A] border border-[#3A3A3A] text-white font-medium outline-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:border-[#5A5A5A] transition-all"
                >
                  <option value="" disabled>
                    Select Policy
                  </option>

                  <option value={PolicyMode.Blacklist}>Blacklist</option>

                  <option value={PolicyMode.Allowlist}>Allowlist</option>
                </select>

                <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-400">▼</div>
              </div>

              {/* Submit Button */}
              <button
                disabled={!selectedPolicyAction || selectedItems.size === 0}
                onClick={async () => {
                  if (selectedPolicyAction === PolicyMode.Blacklist) {
                    openPolicyModal(PolicyMode.Blacklist);
                  }

                  if (selectedPolicyAction === PolicyMode.Allowlist) {
                    await handleSubmitPolicy(PolicyMode.Allowlist, []);
                  }

                  setSelectedPolicyAction('');
                }}
                className={`bg-red-600 px-4 py-2 rounded text-sm br-15 ${!selectedPolicyAction || selectedItems.size === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-red-700'} transition-colors`}
              >
                Submit
              </button>
            </div>
          </div>
        </div>

        {/* DataTable */}
        <div className="overflow-x-auto mt-10">
          <DataTable
            columns={columns}
            data={assetsList}
            loading={loading}
            alternateRowColors={true}
            hoverEffect={true}
            containerStyle={{ background: 'transparent', borderRadius: 0, boxShadow: 'none' }}
            headerStyle={{
              backgroundColor: '#17181B',
              fontSize: 13,
              fontWeight: 700,
              padding: '0.75rem 0',
              borderBottom: '2px solid #2a2a2a',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              minHeight: 56,
              alignItems: 'center',
            }}
            rowStyle={{
              fontSize: 15,
              padding: '1.25rem 0',
              borderBottom: '1px solid #3a3a3a',
              minHeight: 60,
              cursor: 'pointer',
            }}
          />
        </div>

        {/* Pagination */}
        <Pagination currentPage={currentPage} totalPages={totalPages} loading={loading} onPageChange={handlePageChange} />
      </div>

      {/* Policy Modal */}
      <SoftwarePolicyModal
        show={showPolicyModal}
        policyMode={policyMode}
        selectedItemCount={selectedItems.size}
        selectAllGroups={selectAllGroups}
        tenantGroups={tenantGroups}
        selectedGroups={selectedGroups}
        submittingPolicy={submittingPolicy}
        onClose={() => setShowPolicyModal(false)}
        onSubmit={handleSubmitPolicy}
        onSelectAllGroupsChange={(checked) => {
          setSelectAllGroups(checked);
          if (checked) {
            setSelectedGroups(new Set(tenantGroups.map((g) => g.tenantGroupId)));
          } else {
            setSelectedGroups(new Set());
          }
        }}
        onGroupToggle={(groupId, checked) => {
          const newGroups = new Set(selectedGroups);
          if (checked) {
            newGroups.add(groupId);
          } else {
            newGroups.delete(groupId);
          }
          if (newGroups.size !== tenantGroups.length && selectAllGroups) {
            setSelectAllGroups(false);
          }
          if (tenantGroups.length > 0 && newGroups.size === tenantGroups.length && !selectAllGroups) {
            setSelectAllGroups(true);
          }
          setSelectedGroups(newGroups);
        }}
      />
    </div>
  );
};

export default AssetsTable;
